import type { FamilyRelationship } from './member-form-state';

export interface FamilyPerson {
  id: string;
  firstName: string;
  middleName: string | null;
  lastName: string;
  gender: 'female' | 'male' | 'other' | 'prefer_not_to_say' | null;
  status: string;
  photoUrl: string | null;
}

export interface FamilyLink {
  id: string;
  relationship: FamilyRelationship;
  notes: string | null;
  inferred?: boolean;
  degree?: number;
  attachedToMemberId?: string | null;
  related: FamilyPerson;
}

export interface InferredPeerLink {
  fromMemberId: string;
  toMemberId: string;
  relationship: 'sibling';
  inferred: true;
}

export interface FamilyTreeData {
  memberId: string;
  depth: number;
  exploredDepth: number;
  hasMore: boolean;
  nextDepth: number | null;
  relativeCount: number;
  parents: FamilyLink[];
  spouses: FamilyLink[];
  siblings: FamilyLink[];
  children: FamilyLink[];
  niecesNephews: FamilyLink[];
  cousins: FamilyLink[];
  grandparents: FamilyLink[];
  grandchildren: FamilyLink[];
  unclesAunts: FamilyLink[];
  other: FamilyLink[];
  peerLinks: InferredPeerLink[];
}

export type FamilyView = 'tree' | 'grid' | 'list';

export const displayPersonName = (person: {
  firstName: string;
  middleName?: string | null;
  lastName: string;
}) => [person.firstName, person.middleName, person.lastName].filter(Boolean).join(' ');

export type FamilyTreeGroupKey =
  | 'grandparents'
  | 'parents'
  | 'unclesAunts'
  | 'spouses'
  | 'siblings'
  | 'children'
  | 'niecesNephews'
  | 'cousins'
  | 'grandchildren'
  | 'other';

export const FAMILY_TREE_GROUPS: Array<{ key: FamilyTreeGroupKey; labelKey: string }> = [
  { key: 'grandparents', labelKey: 'members.family.tree.grandparents' },
  { key: 'parents', labelKey: 'members.family.tree.parents' },
  { key: 'unclesAunts', labelKey: 'members.family.tree.unclesAunts' },
  { key: 'spouses', labelKey: 'members.family.tree.spouses' },
  { key: 'siblings', labelKey: 'members.family.tree.siblings' },
  { key: 'children', labelKey: 'members.family.tree.children' },
  { key: 'niecesNephews', labelKey: 'members.family.tree.niecesNephews' },
  { key: 'cousins', labelKey: 'members.family.tree.cousins' },
  { key: 'grandchildren', labelKey: 'members.family.tree.grandchildren' },
  { key: 'other', labelKey: 'members.family.tree.other' },
];

export const familyLinkCount = (tree: FamilyTreeData) => tree.relativeCount;
